import React, { useState, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { ChevronDown } from "lucide-react";

const FilterButton = ({
  text = "Filter",
  options = [],
  selectedValue,
  onSelect,
  disabled = false,
  className = "",
  contentComponent = null, // 🔹 New optional prop for nested dropdowns
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);
  const btnRef = useRef(null);
  const [menuPos, setMenuPos] = useState({ top: 0, left: 0, width: 0 });

  // Toggle dropdown and calculate position
  const toggleDropdown = () => {
    if (disabled) return;
    setIsOpen((prev) => {
      const next = !prev;
      if (next && btnRef.current) {
        const rect = btnRef.current.getBoundingClientRect();
        setMenuPos({
          top: rect.bottom + 5,
          left: rect.left - 12,
          width: rect.width,
        });
      }
      return next;
    });
  };

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        // Prevent closing if click inside another dropdown portal
        if (event.target.closest("[data-ha-menu='true']")) return;
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <div className="relative" ref={dropdownRef}>
      {/* 🔹 Scoped animation styles (same as HeaderActions) */}
      <style>{`
        @keyframes ha-in-left {
          from { opacity: 0; transform: translateX(-10px) }
          to { opacity: 1; transform: translateX(0) }
        }
        .ha-dropdown { animation: ha-in-left 200ms ease-out both; }
      `}</style>

      {/* 🔹 Button — identical to Button.jsx (minus full rounding) */}
      <button
        ref={btnRef}
        onClick={toggleDropdown}
        disabled={disabled}
        className={`
          min-w-[clamp(8rem,8vw,10rem)] h-10 px-fluid-button-x py-fluid-button-y
          inline-flex items-center justify-center gap-fluid-1.5
          font-roboto text-fluid-sm font-semibold
          rounded-lg transition-none focus:outline-none
          bg-acs-primary-navy  text-[#F8FAFF]
          dark:hover:bg-[#1E2A74] dark:active:bg-[#16225e]
          shadow-sm hover:shadow-md active:shadow-inner
          dark:bg-acs-primary-navy-dark dark:text-acs-dark-text
          ${disabled ? "opacity-50 cursor-not-allowed" : ""}
          ${className}
        `}
      >
        <span className="truncate">{selectedValue || text}</span>
        <ChevronDown
          size={16}
          className={`transition-transform ${isOpen ? "rotate-180" : "rotate-0"}`}
        />
      </button>

      {/* 🔹 Dropdown — supports both simple lists and custom nested content */}
      {isOpen &&
        createPortal(
          <div
            data-ha-menu="true"
            className="ha-dropdown fixed z-[9999] border border-gray-200 dark:border-acs-dark-border 
                       rounded-md shadow-lg bg-white dark:bg-acs-dark-bg"
            style={{
              top: menuPos.top,
              left: menuPos.left,
              minWidth: Math.max(160, menuPos.width),
            }}
          >
            {contentComponent ? (
              // ✅ If custom nested component is passed, render that
              <div className="relative">{contentComponent}</div>
            ) : (
              // ✅ Default flat options list (original behavior)
              <div className="flex flex-col p-fluid-2">
                {options.map((option, index) => (
                  <button
                    key={index}
                    className={`flex items-center gap-fluid-1 px-fluid-3 py-fluid-1.5 
                                text-gray-600 dark:text-acs-dark-text text-fluid-xs sm:text-fluid-sm 
                                hover:bg-gray-50 dark:hover:bg-acs-dark-hover rounded-md transition-none`}
                    onClick={() => {
                      setIsOpen(false);
                      onSelect(option);
                    }}
                  >
                    <span className="whitespace-nowrap">{option}</span>
                  </button>
                ))}
              </div>
            )}
          </div>,
          document.body
        )}
    </div>
  );
};

export default FilterButton;
